// On a counter fridge every shelf hangs its price tags on the one top rail, so tags of products that
// sit above each other on different shelves land on the same spot. This nudges a line of tags apart
// along the rail the way a real counter's tags get pushed along theirs: each tag moves as little as
// possible from its product, and a group that has to move shares the shift evenly.

// visual px kept between neighbouring tags
const TAG_GAP = 4

// `tags` are `{ key, center, width }` in one horizontal coordinate space (visual px, i.e. after any
// CSS scale). Returns `{ [key]: dx }` — the shift each tag needs; a tag that stays put is absent.
export function spreadTags(tags, railWidth) {
  const sorted = tags.filter(tag => tag.width > 0).sort((a, b) => a.center - b.center)

  // A run is a group of tags packed edge to edge; `offset` is a tag's centre from the run's left edge.
  // A tag that would touch the run before it joins that run, and the merged run is re-centred, so a
  // chain of collisions resolves into one evenly shared shift instead of a cascade to the right.
  const runs = []
  sorted.forEach(tag => {
    let run = { start: tag.center - tag.width / 2, width: tag.width, members: [{ tag, offset: tag.width / 2 }] }
    while (runs.length && runs[runs.length - 1].start + runs[runs.length - 1].width + TAG_GAP > run.start) {
      const prev = runs.pop()
      const step = prev.width + TAG_GAP
      const members = prev.members.concat(run.members.map(m => ({ tag: m.tag, offset: m.offset + step })))
      // the left edge that keeps the run closest to its products: the mean of the edges each tag wants
      const start = members.reduce((acc, m) => acc + m.tag.center - m.offset, 0) / members.length
      run = { start, width: step + run.width, members }
    }
    runs.push(run)
  })

  // Keep the line inside the rail; a line wider than the rail packs from the left edge and overflows
  // right. Runs never touch, so both passes move whole runs.
  let edge = 0
  runs.forEach(run => {
    run.start = Math.max(run.start, edge)
    edge = run.start + run.width + TAG_GAP
  })
  // summed over the tags, not the runs: the two float paths differ, and on a line that fills the rail
  // to the pixel that difference decides whether the backward pass runs at all
  const lineWidth = sorted.reduce((acc, tag) => acc + tag.width, 0) + TAG_GAP * (sorted.length - 1)
  if (lineWidth <= railWidth) {
    let limit = railWidth
    for (let i = runs.length - 1; i >= 0; i--) {
      runs[i].start = Math.min(runs[i].start, limit - runs[i].width)
      limit = runs[i].start - TAG_GAP
    }
  }

  const shifts = {}
  runs.forEach(run =>
    run.members.forEach(m => {
      const dx = Math.round(run.start + m.offset - m.tag.center)
      if (dx) shifts[m.tag.key] = dx
    })
  )
  return shifts
}
